/**
 * Module-level shared state for cross-card (Lab test, Test date)
 * duplicate detection in the AI-transcript LI flow.
 *
 * Multiple DigitiseManuallyCards can be open simultaneously in the
 * rail (via "+ Add another test" spawning parallel cards). The
 * catalog contract forbids the SAME (labTest, testDate) combination
 * being committed twice — either from a single card or across two
 * different cards on the same call.
 *
 * Cards register their current pick via `setCombo(formId, ltCode,
 * date)` on state changes; on unmount / close they call
 * `clearCombo(formId)`. Any card can query
 * `isDuplicate(formId, ltCode, date)` to see whether ANOTHER card
 * already owns the combo. `subscribe(cb)` lets each card force a
 * re-render when the shared map changes so the "duplicate" gate on
 * Save + inline warning stay live.
 *
 * Not React state — no prop drilling, no cascading re-renders on
 * unrelated section switches. The mini subscription bus scopes
 * updates to LI cards that opted in.
 *
 * ═════════════════════════════════════════════════════════════════
 *  LOCKED — (Lab test, Test date) uniqueness across LI cards
 *  (2026-09-21)
 * ═════════════════════════════════════════════════════════════════
 *  This module and its consumers (DigitiseManuallyCard's
 *  setCombo/clearCombo/isDuplicate wiring around `test.labTest.ltCode`
 *  + `test.testDate`) are LOCKED. Two cards may never save with the
 *  same (ltCode, date) pair — the card lights an inline "duplicate"
 *  warning and Save is blocked. Do NOT weaken silently. If a change
 *  genuinely needs to alter or remove this rule, surface it BEFORE
 *  editing. See feedback_transcript_blank_guard_locked.md.
 */

const combos = new Map(); // formId -> "ltCode|date"
const listeners = new Set();

const notify = () => {
  listeners.forEach((cb) => {
    try { cb(); } catch (_) { /* swallow — listener errors mustn't break the bus */ }
  });
};

export const setCombo = (formId, ltCode, date) => {
  if (formId == null) return;
  const key = ltCode && date ? `${ltCode}|${date}` : "";
  if (key) combos.set(String(formId), key);
  else combos.delete(String(formId));
  notify();
};

export const clearCombo = (formId) => {
  if (formId == null) return;
  combos.delete(String(formId));
  notify();
};

export const isDuplicate = (formId, ltCode, date) => {
  if (!ltCode || !date) return false;
  const key = `${ltCode}|${date}`;
  const self = String(formId);
  for (const [id, combo] of combos) {
    if (id !== self && combo === key) return true;
  }
  return false;
};

export const subscribe = (cb) => {
  listeners.add(cb);
  return () => { listeners.delete(cb); };
};

// Test-only reset — safe no-op in production.
export const _reset = () => {
  combos.clear();
  listeners.clear();
};
